import React from "react";

import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";

import { toggleTheme } from "../slices/themeSlice";

import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import MovieIcon from "@mui/icons-material/Movie";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import GroupIcon from "@mui/icons-material/Group";
import PaidIcon from "@mui/icons-material/Paid";

import { useNetworkStatus } from "../hooks/useNetworkStatus";

import { Flip, toast } from "react-toastify";
import { useLogoutTwitchUserMutation } from "../slices/twitchApiSlice";

import { logout } from "../slices/userSlice";
import { Skeleton } from "@mui/material";

const MaterialUISwitch = styled(Switch)(({ theme }) => ({
  width: 62,
  height: 34,
  padding: 7,
  "& .MuiSwitch-switchBase": {
    margin: 1,
    padding: 0,
    transform: "translateX(6px)",
    "&.Mui-checked": {
      color: "#fff",
      transform: "translateX(22px)",
      "& .MuiSwitch-thumb:before": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
          "#fff",
        )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`,
      },
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: theme.palette.mode === "dark" ? "#8796A5" : "#aab4be",
      },
    },
  },
  "& .MuiSwitch-thumb": {
    backgroundColor: theme.palette.mode === "dark" ? "#003892" : "white",
    width: 32,
    height: 32,
    "&::before": {
      content: "''",
      position: "absolute",
      width: "100%",
      height: "100%",
      left: 0,
      top: 0,
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
        "black",
      )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
    },
  },
  "& .MuiSwitch-track": {
    opacity: 1,
    backgroundColor: theme.palette.mode === "dark" ? "#8796A5" : "#aab4be",
    borderRadius: 20 / 2,
  },
}));

function Header() {
  const isOnline = useNetworkStatus();
  const { user, hasToken } = useSelector((state) => state.user);
  const { darkMode } = useSelector((state) => state.theme);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const isUserLoading = !user && hasToken;

  const [logoutApiCall] = useLogoutTwitchUserMutation();

  const logoutHandler = async () => {
    try {
      const response = await logoutApiCall().unwrap();
      dispatch(logout());
      handleCloseTwitchUserMenu();
      navigate("/");
      toast.success(`${response.message}`, {
        position: "bottom-left",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
        transition: Flip,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const [anchorElPosts, setAnchorElPosts] = React.useState(null);
  const [anchorElGames, setAnchorElGames] = React.useState(null);
  const [anchorElUsers, setAnchorElUsers] = React.useState(null);
  const [anchorElTwitchUser, setAnchorElTwithchUser] = React.useState(null);

  const handleOpenTwitchUserMenu = (event) => {
    if (!isOnline) {
      toast.error("Unavailable offline", {
        position: "bottom-left",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "dark",
        transition: Flip,
      });
    }
    if (isOnline) {
      setAnchorElTwithchUser(event.currentTarget);
    }
  };

  const handleCloseTwitchUserMenu = () => {
    setAnchorElTwithchUser(null);
  };

  const handleCloseTwitchUserMenuAndToPath = (pathTo = "") => {
    setAnchorElTwithchUser(null);
    if (pathTo) {
      navigate(pathTo);
    }
  };

  const handleOpenPostsMenu = (event) => {
    setAnchorElPosts(event.currentTarget);
  };

  const handleClosePostsMenu = () => {
    setAnchorElPosts(null);
  };

  const handleClosePostsMenuAndToPath = (pathTo = "") => {
    setAnchorElPosts(null);
    if (pathTo) {
      navigate(pathTo);
    }
  };

  const handleOpenGamesMenu = (event) => {
    setAnchorElGames(event.currentTarget);
  };

  const handleCloseGamesMenu = () => {
    setAnchorElGames(null);
  };

  const handleCloseGamesMenuAndToPath = (pathTo = "") => {
    setAnchorElGames(null);
    if (pathTo) {
      navigate(pathTo);
    }
  };

  const handleOpenUsersMenu = (event) => {
    setAnchorElUsers(event.currentTarget);
  };

  const handleCloseUsersMenu = () => {
    setAnchorElUsers(null);
  };

  const handleCloseUsersMenuAndToPath = (pathTo = "") => {
    setAnchorElUsers(null);
    if (pathTo) {
      navigate(pathTo);
    }
  };

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
    localStorage.setItem("darkMode", !darkMode);
  };

  return (
    <AppBar
      position="static"
      id="custom-app-bar"
      sx={{ display: "flex", justifyContent: "center", minHeight: "64px" }}
    >
      <Container
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "10px",
            height: "100%",
          }}
        >
          <Link
            to="/"
            style={{
              textDecoration: "none",
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <img
              src="/images/storm-120.png"
              alt="storm"
              width="44px"
              height="44px"
              style={{ overflow: "hidden", marginRight: "0.5rem" }}
            />
            <Typography
              sx={{
                display: { xs: "none", md: "flex" },
                color: "white",
              }}
              variant="h6"
            >
              STORM LIVE
            </Typography>
          </Link>

          {isUserLoading ? (
            <>
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ bgcolor: "gray", padding: "8px" }}
              />
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ bgcolor: "gray", padding: "8px" }}
              />
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ bgcolor: "gray", padding: "8px" }}
              />
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ bgcolor: "gray", padding: "8px" }}
              />
              <Skeleton
                variant="text"
                width={60}
                sx={{
                  bgcolor: "gray",
                  fontSize: "1rem",
                  display: { xs: "none", md: "flex" },
                }}
              />
            </>
          ) : (
            <>
              <IconButton
                id="demo-customized-button"
                aria-controls={
                  anchorElPosts ? "demo-customized-menu" : undefined
                }
                aria-haspopup="true"
                aria-expanded={anchorElPosts ? "true" : undefined}
                variant="contained"
                onClick={handleOpenPostsMenu}
              >
                <MovieIcon
                  sx={{
                    color: location.pathname.startsWith("/post")
                      ? "#a23ae8"
                      : "white",
                  }}
                />
              </IconButton>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElPosts}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                open={Boolean(anchorElPosts)}
                onClose={handleClosePostsMenu}
              >
                <MenuItem
                  onClick={() => handleClosePostsMenuAndToPath("posts")}
                >
                  <Typography>Ratings</Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => handleClosePostsMenuAndToPath("posts/stats")}
                >
                  <Typography>Stats</Typography>
                </MenuItem>
                {user &&
                  (user.roles.includes("admin") ||
                    user.roles.includes("superadmin")) && (
                    <MenuItem
                      onClick={() =>
                        handleClosePostsMenuAndToPath("posts/dashboard")
                      }
                    >
                      <Typography>Dashboard</Typography>
                    </MenuItem>
                  )}
                {user &&
                  (user.roles.includes("admin") ||
                    user.roles.includes("superadmin")) && (
                    <MenuItem
                      onClick={() =>
                        handleClosePostsMenuAndToPath("post/tags/dashboard")
                      }
                    >
                      <Typography>Tags</Typography>
                    </MenuItem>
                  )}
              </Menu>
              <IconButton
                id="demo-customized-button"
                aria-controls={
                  anchorElGames ? "demo-customized-menu" : undefined
                }
                aria-haspopup="true"
                aria-expanded={anchorElGames ? "true" : undefined}
                variant="contained"
                onClick={handleOpenGamesMenu}
              >
                <SportsEsportsIcon
                  sx={{
                    color: location.pathname.startsWith("/game")
                      ? "#3ae87a"
                      : "white",
                  }}
                />
              </IconButton>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElGames}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                open={Boolean(anchorElGames)}
                onClose={handleCloseGamesMenu}
              >
                <MenuItem
                  onClick={() => handleCloseGamesMenuAndToPath("games")}
                >
                  <Typography>Ratings</Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => handleCloseGamesMenuAndToPath("games/stats")}
                >
                  <Typography>Stats</Typography>
                </MenuItem>
                {user &&
                  (user.roles.includes("admin") ||
                    user.roles.includes("superadmin")) && (
                    <MenuItem
                      onClick={() =>
                        handleCloseGamesMenuAndToPath("games/dashboard")
                      }
                    >
                      <Typography>Dashboard</Typography>
                    </MenuItem>
                  )}
              </Menu>
              {/* Tag Dashboard IconButton for Admins */}

              <IconButton
                id="demo-customized-button"
                aria-controls={
                  anchorElUsers ? "demo-customized-menu" : undefined
                }
                aria-haspopup="true"
                aria-expanded={anchorElUsers ? "true" : undefined}
                variant="contained"
                onClick={handleOpenUsersMenu}
              >
                <GroupIcon
                  sx={{
                    color: location.pathname.startsWith("/user")
                      ? "#e83a5d"
                      : "white",
                  }}
                />
              </IconButton>
              <Menu
                sx={{ mt: "45px" }}
                id="menu-appbar"
                anchorEl={anchorElUsers}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                open={Boolean(anchorElUsers)}
                onClose={handleCloseUsersMenu}
              >
                <MenuItem
                  onClick={() => handleCloseUsersMenuAndToPath("users")}
                >
                  <Typography>Ratings</Typography>
                </MenuItem>

                <MenuItem
                  onClick={() =>
                    handleCloseUsersMenuAndToPath("users/post-comments")
                  }
                >
                  <Typography>Post Comments</Typography>
                </MenuItem>

                <MenuItem
                  onClick={() =>
                    handleCloseUsersMenuAndToPath("users/game-comments")
                  }
                >
                  <Typography>Game Comments</Typography>
                </MenuItem>

                <MenuItem
                  onClick={() =>
                    handleCloseUsersMenuAndToPath("users/user-comments")
                  }
                >
                  <Typography>User Comments</Typography>
                </MenuItem>
                {user && user.roles.includes("admin") && (
                  <MenuItem
                    onClick={() =>
                      handleCloseUsersMenuAndToPath("users/dashboard")
                    }
                  >
                    <Typography>Dashboard</Typography>
                  </MenuItem>
                )}
                {/* {user && user.roles.includes("superadmin") && (
                  <MenuItem
                    onClick={() =>
                      handleCloseUsersMenuAndToPath("users/badges")
                    }
                  >
                    <Typography>Badges</Typography>
                  </MenuItem>
                )} */}
              </Menu>
              {/* <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/support")}
              >
                <IconButton
                  id='demo-customized-button'
                  aria-controls={
                    anchorElUsers ? "demo-customized-menu" : undefined
                  }
                  aria-haspopup='true'
                  aria-expanded={anchorElUsers ? "true" : undefined}
                  variant='contained'
                >
                  <PaidIcon
                    sx={{
                      color: location.pathname.startsWith("/support")
                        ? "gold"
                        : "white",
                    }}
                  />
                </IconButton>
                <Typography
                  sx={{
                    color: location.pathname.startsWith("/support")
                      ? "gold"
                      : "white",
                    display: { xs: "none", md: "flex" },
                  }}
                >
                  support
                </Typography>
              </Box> */}
            </>
          )}
        </Box>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "10px",
            height: "100%",
          }}
        >
          {isUserLoading ? (
            <Skeleton
              variant="circular"
              width={40}
              height={40}
              sx={{ bgcolor: "gray" }}
            />
          ) : (
            <>
              <MaterialUISwitch
                name="mode-switcher"
                onClick={handleThemeToggle}
                checked={darkMode}
              />
              {user ? (
                <>
                  <Tooltip title="Open settings">
                    <IconButton
                      onClick={handleOpenTwitchUserMenu}
                      sx={{ p: 0 }}
                    >
                      <Avatar alt="LIFETOSE" src={user.profile_image_url} />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    sx={{ mt: "45px" }}
                    id="menu-appbar"
                    anchorEl={anchorElTwitchUser}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "right",
                    }}
                    open={Boolean(anchorElTwitchUser)}
                    onClose={handleCloseTwitchUserMenu}
                  >
                    {user && (
                      <MenuItem
                        onClick={() =>
                          handleCloseTwitchUserMenuAndToPath("/profile")
                        }
                      >
                        <Typography>Profile</Typography>
                      </MenuItem>
                    )}
                    <MenuItem onClick={logoutHandler}>
                      <Typography>Log Out</Typography>
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                // <>
                //   <Tooltip title='Open settings'>
                //     <IconButton
                //       onClick={handleOpenTwitchUserMenu}
                //       sx={{ p: 0 }}
                //     >
                //       <Avatar alt='LIFETOSE' />
                //     </IconButton>
                //   </Tooltip>
                //   <Menu
                //     sx={{ mt: "45px" }}
                //     id='menu-appbar'
                //     anchorEl={anchorElTwitchUser}
                //     anchorOrigin={{
                //       vertical: "top",
                //       horizontal: "right",
                //     }}
                //     keepMounted
                //     transformOrigin={{
                //       vertical: "top",
                //       horizontal: "right",
                //     }}
                //     open={Boolean(anchorElTwitchUser)}
                //     onClose={() => handleCloseTwitchUserMenu()}
                //   >
                //     <MenuItem
                //       onClick={() => {
                //         window.location.href =
                //           process.env.NODE_ENV === "production"
                //             ? "https://storm-tv.onrender.com/api/twitch/login"
                //             : "http://localhost:5000/api/twitch/login";
                //       }}
                //     >
                //       <Typography textAlign='center'>Log in</Typography>
                //     </MenuItem>
                //   </Menu>
                // </>
                <>
                  <Link
                    to="/login"
                    style={{
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <img
                      src="/images/twitch.png"
                      alt="storm"
                      width="44px"
                      height="44px"
                      style={{ overflow: "hidden" }}
                    />
                  </Link>
                </>
              )}
            </>
          )}
        </Box>
      </Container>
    </AppBar>
  );
}

export default Header;
